import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { createAuthTokensService } from '../lib/auth/AuthTokensService';
import { setupAuthInterceptor } from '../lib/auth/authInterceptor';
import { useAuth as useIframeAuth } from '../lib/auth/useAuth';
import { initializeHiddenIframeRefresh } from '../lib/auth/hiddenIframeRefresh';
import { authService } from '../lib/auth/authService';
import { authApi, type User, type LoginRequest, api } from '../lib/api';
import toast from 'react-hot-toast';
import { useIntercom } from '@darwin-launchpad/legacy-shared/intercom';
import { useDarwinFeedbackToolbar } from '@darwin-launchpad/legacy-shared/feedback-toolbar';
import { feedbackToolbarOptions } from '../lib/feedbackToolbarOptions';
import { initTelemetry, setTelemetryUser, clearTelemetryUser } from '@darwin-launchpad/legacy-shared/telemetry';

// Session Replay, the same settings as every Darwin app: 5 % of production
// sessions with all text masked, every staging session with inputs masked.
// Nothing is blocked — layout, clicks and (on staging) text are all visible.
initTelemetry({
  service: 'bid-checker',
  datadogService: 'darwin-launchpad-bid-checker',
  sessionReplay: {
    sampleRate: { production: 0.05, staging: 1 },
  },
});

interface IframeAuthContextType {
  user: User | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (data: { current_password: string; new_password: string }) => Promise<void>;
  // Additional iframe auth properties
  credentials: any;
  rolesFromToken: string[] | null;
  authTenantId: string;
}

const IframeAuthContext = createContext<IframeAuthContextType | undefined>(undefined);

interface IframeAuthProviderProps {
  children: ReactNode;
  authDomain: string;
  apiBaseUrl: string;
  onNavigateToLogin: () => void;
  getQueryParam?: (param: string) => string | null;
  darwinLoginUrl?: string;
}

export const IframeAuthProvider: React.FC<IframeAuthProviderProps> = ({
  children,
  authDomain,
  apiBaseUrl,
  onNavigateToLogin,
  getQueryParam,
  darwinLoginUrl,
}) => {
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useIntercom(user);
  // The customer feedback toolbar, hidden until the lane's `feedback_toolbar` flag is on.
  useDarwinFeedbackToolbar(user, feedbackToolbarOptions());

  // Initialize auth service and interceptor
  useEffect(() => {
    const isDevBypass = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

    createAuthTokensService(authDomain, darwinLoginUrl);

    // Initialize hidden iframe refresh service if darwinLoginUrl is provided
    if (darwinLoginUrl && !isDevBypass) {
      initializeHiddenIframeRefresh(authDomain, darwinLoginUrl);
    }

    setupAuthInterceptor(
      apiBaseUrl,
      authService,
      onNavigateToLogin,
      getQueryParam,
      api
    );
  }, [authDomain, apiBaseUrl, onNavigateToLogin, getQueryParam, darwinLoginUrl]);

  // Use iframe auth hook
  const { credentials, rolesFromToken, authTenantId, isLoading: isIframeLoading, isAuthenticated } = useIframeAuth();

  // Debug logging
  useEffect(() => {
    console.log('IframeAuthProvider state:', {
      isAuthenticated,
      hasCredentials: !!credentials,
      hasAccessToken: !!credentials?.access_token?.token,
      isIframeLoading,
      user: !!user
    });
  }, [isAuthenticated, credentials, isIframeLoading, user]);

  // Fetch user data when credentials are available
  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery(
    // Keyed on the token: a /auth/me that failed on a stale token (401, then a
    // refused refresh) runs again once a working one arrives. With the plain
    // key and retry: false it never re-ran, and the session stayed anonymous.
    ['current-user', credentials?.access_token?.token],
    authApi.getCurrentUser,
    {
      enabled: isAuthenticated && !!credentials?.access_token?.token,
      retry: false,
      onSuccess: (data) => {
        setUser(data);
        setIsInitialized(true);
      },
      onError: (error: any) => {
        console.error('Failed to fetch user:', error);
        if (error.response?.status === 401) {
          // Token is invalid, let the interceptor handle it
          setUser(null);
        }
        setIsInitialized(true);
      },
    }
  );

  // Initialize immediately if no credentials
  useEffect(() => {
    if (!isAuthenticated && !isIframeLoading && !isInitialized) {
      setIsInitialized(true);
    }
  }, [isAuthenticated, isIframeLoading, isInitialized]);

  // Update user when userData changes
  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData]);

  // Clear user when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
    }
  }, [isAuthenticated]);

  // Set or clear Datadog user/tenant context based on auth state
  useEffect(() => {
    if (user) {
      // Identify whenever there is a user. The token's tenant is preferred, the
      // profile's otherwise: gating on the token tenant left a signed-in user
      // with none unidentified for the whole session.
      const profile = user as { tenant_id?: string; current_tenant?: { id?: string } };
      setTelemetryUser(user.id, authTenantId || profile.current_tenant?.id || profile.tenant_id || '', {
        tenantName: (user as any).current_tenant?.name,
        userName: [(user as any).first_name, (user as any).last_name].filter(Boolean).join(' ') || user.full_name || undefined,
        userEmail: user.email,
        roles:
          user.roles ??
          (user as { current_tenant?: { roles?: string[] } }).current_tenant?.roles ??
          rolesFromToken ??
          undefined,
      });
    } else {
      clearTelemetryUser();
    }
  }, [user, authTenantId]);

  // Login mutation
  const loginMutation = useMutation(authApi.login, {
    onSuccess: async (data) => {
      // Store tokens in iframe
      const authTokensService = createAuthTokensService(authDomain, darwinLoginUrl);

      let accessTokenData;

      // Check if data.access_token is already an object with exp field (Darwin format)
      if (typeof data.access_token === 'object' && data.access_token.exp) {
        console.log('✅ Login: Using Darwin credential format with existing exp field');
        accessTokenData = data.access_token;
      } else {
        console.log('🔄 Login: Decoding JWT to extract exp field from token string');
        // Decode JWT to extract expiration time and other fields
        let exp = '';
        let roles = data.user?.roles || [];
        let tenant_id = data.user?.tenant_id || '';

        try {
          const decodedToken = authTokensService.decodeToken(data.access_token);
          exp = new Date(decodedToken.exp * 1000).toISOString(); // Convert Unix timestamp to ISO string
          roles = decodedToken.roles || roles;
          tenant_id = decodedToken.tenant_id || tenant_id;
          console.log('✅ Login: Successfully decoded JWT, exp:', exp);
        } catch (error) {
          console.warn('❌ Login: Failed to decode JWT token during login:', error);
        }

        accessTokenData = {
          token: data.access_token,
          roles: roles,
          tenant_id: tenant_id,
          exp: exp,
        };
      }

      console.log('🔄 Login: Setting credentials with exp field:', accessTokenData.exp);
      await authTokensService.setCredentials({
        access_token: accessTokenData,
        refresh_token: {
          token: data.refresh_token,
          exp: '', // Will be populated from JWT if needed
        },
      });

      setUser(data.user);
      queryClient.setQueryData('current-user', data.user);
      toast.success('Successfully logged in');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Login failed');
    },
  });

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      queryClient.clear();
      toast.success('Successfully logged out');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Logout failed');
    }
  };

  // Update profile mutation
  const updateProfileMutation = useMutation(authApi.updateProfile, {
    onSuccess: (data) => {
      setUser(data);
      queryClient.setQueryData('current-user', data);
      toast.success('Profile updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation(authApi.changePassword, {
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to change password');
    },
  });

  // Context methods
  const login = async (credentials: LoginRequest) => {
    await loginMutation.mutateAsync(credentials);
  };

  const updateProfile = async (data: Partial<User>) => {
    await updateProfileMutation.mutateAsync(data);
  };

  const changePassword = async (data: { current_password: string; new_password: string }) => {
    await changePasswordMutation.mutateAsync(data);
  };

  const resolvedUserRoles = (): string[] => {
    const fromMe =
      user?.roles ??
      (user as { current_tenant?: { roles?: string[] } } | null)?.current_tenant?.roles;
    return fromMe ?? rolesFromToken ?? [];
  };

  const hasRole = (role: string): boolean => {
    return resolvedUserRoles().includes(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    const userRoles = resolvedUserRoles();
    return roles.some(role => userRoles.includes(role));
  };

  // Determine loading state
  const isLoading =
    !isInitialized ||
    isIframeLoading ||
    (isAuthenticated && isUserLoading) ||
    loginMutation.isLoading ||
    updateProfileMutation.isLoading ||
    changePasswordMutation.isLoading;

  const value: IframeAuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: isAuthenticated, // Use iframe auth status directly
    hasRole,
    hasAnyRole,
    updateProfile,
    changePassword,
    credentials,
    rolesFromToken,
    authTenantId,
  };

  return (
    <IframeAuthContext.Provider value={value}>
      {children}
    </IframeAuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(IframeAuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an IframeAuthProvider');
  }
  return context;
};

// Hook for checking permissions
export const usePermissions = () => {
  const { hasRole, hasAnyRole } = useAuth();

  return {
    // Permission checks
    canManageUsers: hasRole('admin'),
    canManageProjects: hasAnyRole(['admin', 'manager']),
    canManageContent: hasAnyRole(['admin', 'manager']),
    canViewAuditLogs: hasAnyRole(['admin', 'auditor']),
    canManageVendors: hasAnyRole(['admin', 'manager', 'evaluator']),
    canEvaluateResponses: hasAnyRole(['admin', 'manager', 'evaluator']),
    canViewReports: hasAnyRole(['admin', 'manager', 'viewer']),
    canManageSettings: hasRole('admin'),
    // Identity Admin (`bidchecker_admin`) and platform `darwin` see every bid
    // in the tenant. The generic `admin` role is the local/dev-bypass alias.
    canSeeAllBids: hasAnyRole(['bidchecker_admin', 'darwin', 'admin']),

    // Role flags
    isAdmin: hasRole('admin'),
    isManager: hasRole('manager'),
    isEvaluator: hasRole('evaluator'),
    isAuditor: hasRole('auditor'),
    isViewer: hasRole('viewer'),
  };
};

export default IframeAuthProvider;